import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages bulk pricing for a product.
 *
 * @typedef {Object} Refs
 * @property {HTMLTableRowElement[]} bulkPricingRow
 *
 * @extends {Component<Refs>}
 */
export default class ProductBulkPricing extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.variantId = this.dataset.variantId ? parseInt(this.dataset.variantId, 10) : null;
    if(!this.variantId) return;
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate.bind(this));
  }

  /**
   * Handles the quantity update event.
   * @param {Event} event - The quantity update event.
   */
  #onQuantityUpdate(event) {
    const { detail } = /** @type {import('@theme/events').QuantitySelectorUpdateEvent} */ (event);
    if (detail?.variantId !== this.variantId) return;
    this.#setActiveRow(detail.quantity);
  }

  /**
   * Sets the active class on the closest matching bulk pricing row.
   * @param {number} quantity - The current quantity.
   */
  #setActiveRow(quantity) {
    const rows = this.refs.bulkPricingRow;
    if (!rows?.length) return;

    /** @type {HTMLTableRowElement | null} */
    let matched = null;

    for (const row of rows) {
      row.classList.remove('active');
      const threshold = parseInt(row.dataset.quantity ?? '', 10);
      if (!isNaN(threshold) && quantity >= threshold) {
        matched = row;
      }
    }

    if (matched) matched.classList.add('active');
  }
}

if (!customElements.get('product-bulk-pricing')) {
  customElements.define('product-bulk-pricing', ProductBulkPricing);
}
